/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { Dimmer, Loader } from '@mairistem/composants';
import { request } from '@mairistem/tools';
import {
  AnnotationSectionPages,
  AnnotationSectionToolbox,
  CanvasContextProvider,
  SectionCanvasAnnotable,
  type TiffPage,
  type TypeActeAnnotable,
  SectionLayers,
  useAnnotationsTypes,
  useImagesPersonnalisees,
  type useLayers,
  useMentions,
} from '@mairistem/administres-utils';

import FICHE_CONFIG from '@/components/Acte/Fiche/ficheConfig';
import { ModalAjouterMention } from '@/components/Mention/Liste';
import { BASE_URL_ETATCIVIL } from '@/constants';

interface ActeDocumentAnnotationProps {
  /** Acte d'où viennent les mentions apposables. */
  identifiantActe?: number;
  /** Type de l'acte, nécessaire à la route des mentions. */
  typeActe?: TypeActeAnnotable;
  /**
   * Calques du document, fournis par l'appelant.
   *
   * Ils ne sont pas chargés ici : la barre d'actions (enregistrer, annuler,
   * historique) vit dans la barre de la page, comme la `FicheBar` d'un acte, et
   * a donc besoin du même état.
   */
  layers: ReturnType<typeof useLayers>;
  /**
   * Pages décodées du document, fournies elles aussi par l'écran : les deux
   * onglets affichent le même document, le décoder ici le rechargerait à chaque
   * bascule.
   */
  pages: Array<TiffPage>;
  /** Seul un TIFF se laisse annoter (cf. `TiffMerger` et `WangReader`). */
  isTiff: boolean;
  chargement?: boolean;
  /** Page affichée, numérotée à partir de 1. */
  pageCourante: number;
  onPageChange: (numero: number) => void;
}

/**
 * Onglet « Annotation du document » de la fiche d'un acte numérisé.
 *
 * Les calques sont chargés par **document** (`documents/{id}/calques`), alors
 * que les mentions apposables et les annotations types se chargent par
 * **acte** : les deux identifiants circulent donc ensemble.
 */
const ActeDocumentAnnotation = (props: ActeDocumentAnnotationProps) => {
  const {
    identifiantActe,
    typeActe,
    layers,
    pages,
    isTiff,
    chargement = false,
    pageCourante,
    onPageChange,
  } = props;

  const images = useImagesPersonnalisees(layers);

  /*
    Les listes de la boîte à outils sont chargées ici, et non par elle : c'est
    cet écran qui offre la saisie d'une mention, et lui seul peut donc
    rafraîchir la liste des mentions apposables après une création.
  */
  const annotationsTypes = useAnnotationsTypes(identifiantActe);
  const mentions = useMentions(identifiantActe || 0, typeActe);

  const [ajoutMentionOuvert, setAjoutMentionOuvert] = React.useState(false);

  /*
    La colonne de rattachement d'une mention est déjà déclarée par la fiche du
    type d'acte : la relire ici évite d'en tenir un second tableau, qui aurait
    pu diverger sans que rien ne le signale.
  */
  const cleMentionActe = typeActe
    ? FICHE_CONFIG[typeActe]?.cleMentionActe
    : undefined;

  // Sans acte ni colonne de rattachement, la mention créée n'appartiendrait à
  // aucun acte : la boîte à outils n'affiche alors pas le bouton.
  const creationMentionPossible =
    !_.isNil(identifiantActe) && !_.isNil(cleMentionActe);

  const [calqueSelectionne, setCalqueSelectionne] = React.useState<
    number | string
  >(0);
  const [infinity, setInfinity] = React.useState(false);

  // L'apposition des mentions est réservée aux offres Infinity.
  React.useEffect(() => {
    request
      .get<unknown, Record<string, boolean>>(`${BASE_URL_ETATCIVIL}/infinity`)
      .then((data) => setInfinity(Boolean(data?.infinity)))
      .catch(() => setInfinity(false));
  }, []);

  const handleSelectionCalque = React.useCallback(
    (idCalque: number | string) => {
      const calque = _.find(layers.data, { identifiant: idCalque });

      if (_.isNil(calque)) {
        setCalqueSelectionne(0);
        return;
      }

      setCalqueSelectionne(idCalque);
      // Suivre le calque jusqu'à sa page, sinon la sélection porte sur un
      // objet invisible.
      if (calque.page !== pageCourante) {
        onPageChange(calque.page);
      }
    },
    [layers.data, pageCourante, onPageChange],
  );

  const handleAjoutCalque = React.useCallback(
    (calque: Parameters<typeof layers.add>[0]) => {
      setCalqueSelectionne(layers.add(calque)?.identifiant);
    },
    [layers.add],
  );

  const occupe = chargement || layers.loading;

  return (
    <CanvasContextProvider>
      <div
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            flex: 1,
            gap: '1em',
            padding: '1em',
            minHeight: 0,
          }}
        >
          <Dimmer active={occupe} inverted>
            <Loader indeterminate />
          </Dimmer>

          <div
            style={{
              display: 'flex',
              flex: 4,
              flexDirection: 'column',
              gap: '1em',
              minHeight: 0,
              minWidth: 0,
            }}
          >
            <div
              style={{
                display: 'flex',
                flex: 3,
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <AnnotationSectionToolbox
                isTiff={isTiff}
                selectedPage={pageCourante}
                onLayerSelected={handleSelectionCalque}
                onAddLayer={handleAjoutCalque}
                layers={layers.data}
                acteConnu={!_.isNil(identifiantActe)}
                annotationsTypes={annotationsTypes}
                mentions={mentions}
                images={images}
                infinity={infinity}
                onAjouterMention={
                  creationMentionPossible
                    ? () => setAjoutMentionOuvert(true)
                    : undefined
                }
              />
            </div>
            <div
              style={{
                display: 'flex',
                flex: 2,
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <SectionLayers
                layers={layers.data}
                selectedLayer={calqueSelectionne}
                onLayerSelected={handleSelectionCalque}
                onDeleteLayer={layers.del}
              />
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              flex: 9,
              flexDirection: 'column',
              minHeight: 0,
              minWidth: 0,
            }}
          >
            <SectionCanvasAnnotable
              isTiff={isTiff}
              pages={pages}
              selectedPage={pageCourante}
              onPageChange={onPageChange}
              layers={layers.data}
              selectedLayer={calqueSelectionne}
              onLayerSelected={handleSelectionCalque}
              onUpdateLayer={layers.update}
              onDeleteLayer={layers.del}
              images={images.data}
              title="IMAGE DU DOCUMENT"
            />
          </div>

          <div
            style={{
              display: 'flex',
              flex: 3,
              flexDirection: 'column',
              minHeight: 0,
              minWidth: 0,
            }}
          >
            <AnnotationSectionPages
              isTiff={isTiff}
              pages={pages}
              selectedPage={pageCourante}
              onPageChange={onPageChange}
              title="PAGE(S) DU DOCUMENT"
            />
          </div>
        </div>
      </div>

      {/*
        La modale de saisie appartient à l'application : elle dépend du
        référentiel ANTS et de la colonne de rattachement, que la boîte à outils
        partagée n'a pas à connaître.
      */}
      {creationMentionPossible && (
        <ModalAjouterMention
          open={ajoutMentionOuvert}
          cleActe={cleMentionActe}
          identifiantActe={identifiantActe}
          onClose={() => setAjoutMentionOuvert(false)}
          onSaved={() => mentions.refresh()}
        />
      )}
    </CanvasContextProvider>
  );
};

export default ActeDocumentAnnotation;
